import { GetProductsParams } from "../types";

type ProductFilterFieldType = "string" | "number" | "boolean";

/**
 * Single source of truth for every field the Products Advanced Search
 * dialog can set (as opposed to quick-search/sort/pagination on the same
 * GetProductsParams object) — drives both hasActiveProductFilters and the
 * "Filters (n)" badge count, so they can't drift apart. Mirrors
 * utils/orderSearchParams.ts's ORDER_SEARCH_FIELD_TYPES pattern.
 */
const PRODUCT_FILTER_FIELD_TYPES: Partial<
    Record<keyof GetProductsParams, ProductFilterFieldType>
> = {
    category_id: "string",
    brand_id: "string",
    is_active: "boolean",
    is_visible: "boolean",
    is_featured: "boolean",
    min_price: "number",
    max_price: "number",
    min_cost_price: "number",
    max_cost_price: "number",
    on_sale_only: "boolean",
    stock_status: "string",
    created_from: "string",
    created_to: "string",
};

const isFieldSet = (value: unknown, type: ProductFilterFieldType): boolean => {
    if (value === undefined || value === null) return false;
    if (type === "string") return value !== "";
    return true; // number/boolean: any value (including false/0) counts as set
};

/** True if any advanced-search filter field is set. */
export function hasActiveProductFilters(params: GetProductsParams): boolean {
    return (
        Object.keys(PRODUCT_FILTER_FIELD_TYPES) as (keyof GetProductsParams)[]
    ).some((key) => isFieldSet(params[key], PRODUCT_FILTER_FIELD_TYPES[key]!));
}

/** How many advanced-search filter fields are set — for the badge on the Filters button. */
export function countActiveProductFilters(params: GetProductsParams): number {
    return (
        Object.keys(PRODUCT_FILTER_FIELD_TYPES) as (keyof GetProductsParams)[]
    ).filter((key) => isFieldSet(params[key], PRODUCT_FILTER_FIELD_TYPES[key]!))
        .length;
}
